import { Updater, useQueryClient } from '@tanstack/react-query';
import {
  APIListQueryData,
  APIListQueryOptions,
  APIListQueryResult,
  useAPIListQuery,
} from 'shared/api/APIListQuery';
import {
  APIQueryOptions,
  APIQueryResult,
  useAPIQuery,
} from 'shared/api/APIQuery';
import { requestCarrierAPI } from 'shared/api/CarrierAPIClient';
import { NotificationVariant } from 'shared/layout/notification-stack/data/NotificationDTO';
import { useSubscriptionPaymentNotificationsSelect } from 'shared/layout/notification-stack/data/NotificationAPI';
import {
  assignedDriverSchema,
  AssignedDriverDTO,
  availableDriverSchema,
  AvailableDriverDTO,
  companyActivitySchema,
  CompanyActivityDTO,
  CompanyActivityParams,
  delayedLoadSchema,
  DelayedLoadDTO,
  DelayedLoadStage,
  delayedLoadsCountSchema,
  DelayedLoadsCountDTO,
  deliveringDriverSchema,
  DeliveringDriverDTO,
  driverSchema,
  DriverDTO,
  driversCountSchema,
  DriversCountDTO,
  unassignedLoadSchema,
  UnassignedLoadDTO,
} from './HomeDTO';

export function useDelayedLoadsCount(
  options?: APIQueryOptions<DelayedLoadsCountDTO>,
): APIQueryResult<DelayedLoadsCountDTO> {
  return useAPIQuery(
    ['home', 'delayed-loads-count'],
    () => requestCarrierAPI('GET /internal/web/dashboard/loads/delayed/count/'),
    { ...options, schema: delayedLoadsCountSchema },
  );
}

export function useDelayedLoads(
  stage: DelayedLoadStage,
  options?: APIListQueryOptions<DelayedLoadDTO>,
): APIListQueryResult<DelayedLoadDTO> {
  return useAPIListQuery(
    ['home', 'delayed-loads', { stage }],
    (page) =>
      requestCarrierAPI([
        `GET /internal/web/dashboard/loads/delayed/${stage}/{?page,params*}`,
        { page, params: { page_size: 10 } },
      ]),
    { ...options, schema: delayedLoadSchema },
  );
}

const UNASSIGNED_LOADS_API_CACHE_KEY = ['home', 'unassigned-loads'];
export function useUnassignedLoads(
  options?: APIListQueryOptions<UnassignedLoadDTO>,
): APIListQueryResult<UnassignedLoadDTO> {
  return useAPIListQuery(
    UNASSIGNED_LOADS_API_CACHE_KEY,
    (page) =>
      requestCarrierAPI([
        `GET /internal/web/dashboard/loads/unassigned/{?page,params*}`,
        { page, params: { page_size: 10 } },
      ]),
    { ...options, schema: unassignedLoadSchema },
  );
}

export function useUnassignedLoadsCache() {
  const queryClient = useQueryClient();
  return {
    invalidateUnassignedLoads: () => {
      void queryClient.invalidateQueries({
        queryKey: UNASSIGNED_LOADS_API_CACHE_KEY,
      });
    },
    updateUnassignedLoads: (
      updater: Updater<
        APIListQueryData<UnassignedLoadDTO> | undefined,
        APIListQueryData<UnassignedLoadDTO> | undefined
      >,
    ) => {
      queryClient.setQueryData<APIListQueryData<UnassignedLoadDTO> | undefined>(
        UNASSIGNED_LOADS_API_CACHE_KEY,
        updater,
      );
    },
  };
}

export function useDriversCount(
  options?: APIQueryOptions<DriversCountDTO>,
): APIQueryResult<DriversCountDTO> {
  return useAPIQuery(
    ['home', 'drivers-count'],
    () => requestCarrierAPI('GET /internal/web/dashboard/drivers/count/'),
    { ...options, schema: driversCountSchema },
  );
}

export function useDeliveredLoadsCount(
  options?: APIQueryOptions<{ count: number }>,
): APIQueryResult<{ count: number }> {
  return useAPIQuery(
    ['home', 'delivered-loads-count'],
    () =>
      requestCarrierAPI('GET /internal/web/dashboard/loads/delivered/count/'),
    { ...options },
  );
}

export function useAvailableDrivers(
  options?: APIListQueryOptions<AvailableDriverDTO>,
): APIListQueryResult<AvailableDriverDTO> {
  return useAPIListQuery(
    ['home', 'available-drivers'],
    (page) =>
      requestCarrierAPI([
        `GET /internal/web/dashboard/drivers/available/{?page,params*}`,
        { page, params: { page_size: 10 } },
      ]),
    { ...options, schema: availableDriverSchema },
  );
}

export function useAssignedDrivers(
  options?: APIListQueryOptions<AssignedDriverDTO>,
): APIListQueryResult<AssignedDriverDTO> {
  return useAPIListQuery(
    ['home', 'assigned-drivers'],
    (page) =>
      requestCarrierAPI([
        `GET /internal/web/dashboard/drivers/assigned/{?page,params*}`,
        { page, params: { page_size: 10 } },
      ]),
    { ...options, schema: assignedDriverSchema },
  );
}

export function useDeliveringDrivers(
  options?: APIListQueryOptions<DeliveringDriverDTO>,
): APIListQueryResult<DeliveringDriverDTO> {
  return useAPIListQuery(
    ['home', 'delivering-drivers'],
    (page) =>
      requestCarrierAPI([
        `GET /internal/web/dashboard/drivers/delivering/{?page,params*}`,
        { page, params: { page_size: 10 } },
      ]),
    { ...options, schema: deliveringDriverSchema },
  );
}

export function useHomeNotifications(
  options?: APIQueryOptions<NotificationVariant[]>,
): APIQueryResult<NotificationVariant[]> {
  const select = useSubscriptionPaymentNotificationsSelect();

  return useAPIQuery(
    ['home', 'notifications'],
    () => requestCarrierAPI('GET /internal/web/dashboard/notifications/'),
    { ...options, select },
  );
}

export function useDrivers(
  search?: string,
  options?: APIListQueryOptions<DriverDTO>,
): APIListQueryResult<DriverDTO> {
  return useAPIListQuery(
    ['home', 'drivers-search', { search }],
    (page) =>
      requestCarrierAPI([
        `GET /internal/web/dashboard/drivers/search/{?page,params*}`,
        { page, params: { page_size: 10, q: search } },
      ]),
    { ...options, schema: driverSchema },
  );
}

export function useCompanyActivities(
  { page, ...params }: CompanyActivityParams = { page: 1, page_size: 10 },
  options?: APIListQueryOptions<CompanyActivityDTO>,
): APIListQueryResult<CompanyActivityDTO> {
  return useAPIListQuery(
    ['home', 'company-activities', { page, params }],
    (page) =>
      requestCarrierAPI([
        `GET /internal/web/company/activities/{?page,params*}`,
        { page, params },
      ]),
    { ...options, schema: companyActivitySchema },
  );
}
